import { useState, useEffect } from 'react';
import { useSelector, useStore as useReduxStore } from 'react-redux';
import { getIsLoggedIn } from '@selectors/auth';
import { useNotifyMeStore } from '@stores/batchGroup/notifyMeStore';
import { useRouter } from 'next/router';
import { getBatchByUIDFromData } from '@selectors/batchGroups';
import customFetcher from '@utils/api-helper/fetch';
import usePlatformEventProps from '@hooks/usePlatformBatchEventProps';
import { PLATFORM_BATCH_PAGE } from '@constants/segment';
import { segment } from '@actions/analytics';
import { useToggleable } from '@stores/toggleables';
import { useSnackbar } from '@stores/snackbar';
import { LOGIN_DRAWER } from '@constants/toggleables';
import notificationsType from './constants';
import { NOTIFY_ME_CLICKED, NOTIFY_ME_REMOVED } from './constants/events';

const useNotifyMe = () => {
  const {
    query: { batchGroupUID }
  } = useRouter();
  const store = useReduxStore();
  const { open: showSnackbar } = useSnackbar();

  const isLoggedIn = useSelector(getIsLoggedIn);
  const [userData, setRequestedData] = useState(null);
  const [isRequestInProgress, setRequestInProgress] = useState(false);
  const {
    setIsNotified,
    isNotified,
    isNotifyRequested,
    setRequestedForNotifyMe,
    notifyMeInfo,
    isNotificationFetched,
    setNotificationFetchedStatus
  } = useNotifyMeStore();

  const batch = useSelector((state) =>
    getBatchByUIDFromData(state, batchGroupUID)
  );

  const {
    isNotified: notifications = {
      [notificationsType.UPCOMING_BATCHES]: false,
      [notificationsType.ONGOING_BATCHES]: false
    }
  } = batch;
  const segmentData = usePlatformEventProps({
    batch,
    isBatch: true
  });

  useEffect(() => {
    setIsNotified(notifications);
    setNotificationFetchedStatus(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribeNotifications = (eventSegmentData) => {
    segment.track(NOTIFY_ME_CLICKED, eventSegmentData);
  };

  const unSubscribeNotifications = (eventSegmentData) => {
    segment.track(NOTIFY_ME_REMOVED, eventSegmentData);
  };

  const { openElement: openLoginModal } = useToggleable(LOGIN_DRAWER);

  const sendNotifyMeRequest = async (data) => {
    if (!isLoggedIn) {
      setRequestInProgress(true);
      setRequestedData(data);
      return openLoginModal();
    }
    try {
      const { is_active, notification_type, touchPoint } = data;

      if (isNotifyRequested) return null;
      setRequestedForNotifyMe(true);

      await customFetcher(`v1/batch/batch_group/${batchGroupUID}/notify/`, {
        dispatch: store.dispatch,
        getState: store.getState,
        requestType: 'POST',
        body: { is_active, notification_type }
      });

      setIsNotified({ ...isNotified, [notification_type]: is_active });
      const eventSegmentData = {
        ...segmentData,
        last_primary_source_section: `${PLATFORM_BATCH_PAGE} - ${touchPoint}`,
        type: 'Batch Group'
      };

      if (is_active) {
        subscribeNotifications(eventSegmentData);
      } else {
        unSubscribeNotifications(eventSegmentData);
      }
    } catch (error) {
      showSnackbar({
        message: error.message || 'Something went wrong. Please try again.',
        type: 'error'
      });
    } finally {
      setRequestedForNotifyMe(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn && isRequestInProgress && userData !== null) {
      setRequestInProgress(false);
      sendNotifyMeRequest(userData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRequestInProgress, userData, isLoggedIn]);

  return {
    sendNotifyMeRequest,
    notifiedNotification: isNotified,
    notifyMeInfo,
    isNotifyRequested,
    isNotificationFetched
  };
};

export default useNotifyMe;
